import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import Card from 'components/card/Card';
import EmptyState from 'components/states/EmptyState';
import ErrorState from 'components/states/ErrorState';
import Loader from 'components/Loader';
import Papa from 'papaparse';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { translate, TranslationKeys } from '../../../i18n';
import { AdminTransactionDto } from '../../../models/payment/dto/get-admin-transactions.dto';
import { tableHeaderTextProps, tableProps, tableRowProps, tableThProps } from '../../../theme/constants';
import { densityTokens } from '../../../theme/chart-config';
import { DensityToggle, useTableDensity } from 'components/DensityToggle';
import { typography } from 'theme/tokens';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import { useNavigateToUserDetails } from '../../_shared/calendar/hooks/useNavigateToUserDetails';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useGetAdminTransactions } from '../hooks/useGetAdminTransactions';
import {
  formatAmount,
  getPaymentMethodLabel,
  getStatusColorScheme,
  getTypeColorScheme,
  sanitizeCsvValue,
} from '../utils/transaction.utils';

const columnHelper = createColumnHelper<AdminTransactionDto>();

const STATUS_LABELS: Record<string, string> = {
  succeeded: 'transaction.succeeded',
  processing: 'transaction.pending',
  requires_action: 'transaction.pending',
  requires_confirmation: 'transaction.pending',
  requires_payment_method: 'transaction.pending',
  canceled: 'transaction.canceled',
  refunded: 'transaction.refunded',
  refunding: 'transaction.refunding',
  pay_later: 'transaction.pay_later',
};

const TYPE_LABELS: Record<string, string> = {
  court_booking: 'transaction.type_court_booking',
  game_joining: 'transaction.type_game_joining',
  event: 'transaction.type_event',
  coaching: 'transaction.type_coaching',
  credit_pack: 'transaction.type_credit_pack',
};

const PAYMENT_METHOD_CONFIG: Record<string, { label: string; color: string }> = {
  wallet: { label: 'transaction.method_wallet', color: 'purple' },
  pay_later: { label: 'transaction.method_pay_later', color: 'blue' },
  stripe: { label: 'transaction.method_stripe', color: 'green' },
  manual_card: { label: 'game.payment_manual_card', color: 'teal' },
  manual_cash: { label: 'game.payment_manual_cash', color: 'teal' },
};

export default function TransactionHistoryTable() {
  const { density, onDensityChange } = useTableDensity('transactions');
  const navigateToUser = useNavigateToUserDetails();
  const { selectedOrganization } = useSelectedOrganization();
  const [currentPage, setCurrentPage] = useState(0);
  const limit = 20;

  // Filter state
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(
    () => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  );
  const [dateTo, setDateTo] = useState('');

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch, statusFilter, paymentMethodFilter, typeFilter, dateFrom, dateTo, selectedOrganization?.id]);

  const { data, isLoading, isInitialLoading, isFetching, isError } = useGetAdminTransactions({
    organizationId: selectedOrganization?.id,
    page: currentPage,
    limit,
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
    paymentMethod: paymentMethodFilter || undefined,
    type: typeFilter || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  // `enabled: Boolean(params.organizationId)`. The `!selectedOrganization`
  // guard below failed safe (it is a complete re-derivation of the single
  // gate), but a store-derived gate is D62's fragile pattern; route the
  // never-issued case through the query's own state instead.
  const readState = classifyRead(isError, isInitialLoading, data);

  const secondaryTextColor = useColorModeValue('gray.500', 'gray.400');

  const columns = useMemo(
    () => [
      columnHelper.accessor('userFirstName', {
        header: translate('transaction.user'),
        cell: (info) => (
          <Box>
            <Text fontWeight="bold" color={textColor} fontSize="sm">
              {info.row.original.userFirstName} {info.row.original.userLastName}
            </Text>
            <Text fontSize="xs" color={secondaryTextColor}>
              {info.row.original.userEmail}
            </Text>
          </Box>
        ),
      }),
      columnHelper.accessor('transactionType', {
        header: translate('transaction.type'),
        cell: (info) => {
          const type = info.getValue();
          return (
            <Badge colorScheme={getTypeColorScheme(type)}>
              {translate((TYPE_LABELS[type] ?? 'transaction.type_unknown') as TranslationKeys)}
            </Badge>
          );
        },
      }),
      columnHelper.accessor('amount', {
        header: translate('transaction.amount'),
        // The quote stays the headline figure, so nothing an operator already
        // reads changes meaning. The second line appears ONLY when the money
        // did something else — a pay-later booking settled below quote, or a
        // partial refund, which deliberately leaves the intent `succeeded` at
        // its full amount. Suppressed for intents that never settled, whose
        // collected figure is a structural 0 rather than a discrepancy.
        cell: (info) => {
          const row = info.row.original;
          const showsCollected =
            row.chargedAmount > 0 && row.collectedAmount !== info.getValue();
          return (
            <Box>
              <Text fontSize="sm" color={textColor}>
                {formatAmount(info.getValue(), row.currency)}
              </Text>
              {showsCollected && (
                <Text fontSize="xs" color={secondaryTextColor}>
                  {translate('transaction.collected')}:{' '}
                  {formatAmount(row.collectedAmount, row.currency)}
                </Text>
              )}
            </Box>
          );
        },
      }),
      columnHelper.accessor('status', {
        header: translate('transaction.status'),
        cell: (info) => {
          const status = info.getValue();
          const isSetup = info.row.original.isSetupIntentPayment;
          return (
            <Badge colorScheme={getStatusColorScheme(status, isSetup)}>
              {translate((STATUS_LABELS[status] ?? status) as TranslationKeys)}
            </Badge>
          );
        },
      }),
      columnHelper.accessor('paidWithWallet', {
        header: translate('transaction.payment_method'),
        cell: (info) => {
          const method = getPaymentMethodLabel(
            info.getValue(),
            info.row.original.isSetupIntentPayment,
            info.row.original.status,
            info.row.original.paymentMethodType,
          );
          const config = PAYMENT_METHOD_CONFIG[method];
          return (
            <Badge colorScheme={config.color}>
              {translate(config.label as TranslationKeys)}
            </Badge>
          );
        },
      }),
      columnHelper.accessor('createdAt', {
        header: translate('transaction.date'),
        cell: (info) => {
          const date = new Date(info.getValue());
          return (
            <Text fontSize="sm" color={textColor}>
              {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Text>
          );
        },
      }),
    ],
    [textColor, secondaryTextColor],
  );

  const table = useReactTable({
    data: data?.transactions ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleExportCsv = useCallback(() => {
    if (!data?.transactions?.length) return;
    const csvData = data.transactions.map((t) => ({
      ID: t.id,
      User: sanitizeCsvValue(`${t.userFirstName} ${t.userLastName}`),
      Email: sanitizeCsvValue(t.userEmail),
      Type: t.transactionType,
      Amount: t.amount != null ? (t.amount / 100).toFixed(2) : '0.00',
      // What the money actually did, for the operator reconciling this file
      // against a bank statement: `Amount` is the quote, `Charged` is what was
      // put through, `Collected` is what the venue kept. Older API responses
      // carry none of the three, hence the null-safe fallbacks.
      Charged: t.chargedAmount != null ? (t.chargedAmount / 100).toFixed(2) : '0.00',
      Refunded: t.refundedAmount != null ? (t.refundedAmount / 100).toFixed(2) : '0.00',
      Collected: t.collectedAmount != null ? (t.collectedAmount / 100).toFixed(2) : '0.00',
      Currency: t.currency,
      Status: t.status,
      'Payment Method': getPaymentMethodLabel(t.paidWithWallet, t.isSetupIntentPayment, t.status, t.paymentMethodType),
      Date: new Date(t.createdAt).toISOString(),
    }));
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [data?.transactions]);

  if (readState === 'never-issued') {
    return (
      <Card flexDirection="column" w="100%" px="0px">
        <ScopePrompt gate="organization" />
      </Card>
    );
  }

  return (
    <Card flexDirection="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
      <Flex px="25px" pt="20px" pb="10px" justify="space-between" align="center">
        <Text color={textColor} {...typography.pageTitle}>
          {translate('transaction.title')}
        </Text>
        <HStack spacing={4}>
          {data?.pagination && (
            <Text fontSize="sm" color={secondaryTextColor}>
              {translate('game.page_of')} {currentPage + 1} {translate('game.of')}{' '}
              {data.pagination.totalPages}
              {data.pagination.totalCount > 0 &&
                ` (${data.pagination.totalCount} ${translate('game.total')})`}
            </Text>
          )}
          <HStack spacing={2}>
            <Button
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
              isDisabled={currentPage === 0 || isLoading || isFetching}
              variant="outline"
            >
              {translate('game.previous')}
            </Button>
            <Button
              size="sm"
              onClick={() => setCurrentPage((p) => p + 1)}
              isDisabled={!data?.pagination?.hasNextPage || isLoading || isFetching}
              variant="outline"
            >
              {translate('game.next')}
            </Button>
          </HStack>
          <DensityToggle tableId="transactions" onChange={onDensityChange} />
          <Button size="sm" colorScheme="brand" onClick={handleExportCsv} isDisabled={!data?.transactions?.length}>
            {translate('transaction.export_csv')}
          </Button>
        </HStack>
      </Flex>

      {/* Filter bar */}
      <Flex px="25px" pb="15px" gap={3} flexWrap="wrap" align="center">
        <Input
          placeholder={translate('transaction.search_placeholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          size="sm"
          maxW="250px"
        />
        <Select
          placeholder={translate('transaction.all_statuses')}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          size="sm"
          maxW="160px"
        >
          <option value="succeeded">{translate('transaction.succeeded')}</option>
          <option value="processing">{translate('transaction.pending')}</option>
          <option value="canceled">{translate('transaction.canceled')}</option>
          <option value="refunded">{translate('transaction.refunded')}</option>
          <option value="refunding">{translate('transaction.refunding')}</option>
          <option value="pay_later">{translate('transaction.pay_later')}</option>
        </Select>
        <Select
          placeholder={translate('transaction.all_methods')}
          value={paymentMethodFilter}
          onChange={(e) => setPaymentMethodFilter(e.target.value)}
          size="sm"
          maxW="160px"
        >
          <option value="stripe">{translate('transaction.method_stripe')}</option>
          <option value="wallet">{translate('transaction.method_wallet')}</option>
          <option value="pay_later">{translate('transaction.method_pay_later')}</option>
        </Select>
        <Select
          placeholder={translate('transaction.all_types')}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          size="sm"
          maxW="180px"
        >
          <option value="court_booking">{translate('transaction.type_court_booking')}</option>
          <option value="game_joining">{translate('transaction.type_game_joining')}</option>
          <option value="event">{translate('transaction.type_event')}</option>
          <option value="coaching">{translate('transaction.type_coaching')}</option>
          <option value="credit_pack">{translate('transaction.type_credit_pack')}</option>
        </Select>
        <Input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          size="sm"
          maxW="160px"
          placeholder={translate('transaction.date_from')}
        />
        <Input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          size="sm"
          maxW="160px"
          placeholder={translate('transaction.date_to')}
        />
      </Flex>

      {readState === 'loading' ? (
        <Loader isLoading />
      ) : readState === 'unreadable' ? (
        // `unreadable`, not the raw `isError`: a BACKGROUND refetch that fails
        // over a ledger page already on screen must keep it rather than
        // blanking the operator's transactions because a refresh was refused
        // (R96-3).
        <ErrorState message={translate('transaction.error_loading')} />
      ) : !data?.transactions?.length ? (
        debouncedSearch ? (
          <EmptyState
            variant="no-results-search"
            searchQuery={debouncedSearch}
            title={translate('transaction.empty_state')}
          />
        ) : (statusFilter || paymentMethodFilter || typeFilter) ? (
          <EmptyState
            variant="no-results-filter"
            title={translate('transaction.empty_state')}
            activeFilters={[
              ...(statusFilter ? [translate((`transaction.${statusFilter}` as any))] : []),
              ...(paymentMethodFilter ? [translate((`transaction.method_${paymentMethodFilter}` as any))] : []),
              ...(typeFilter ? [translate((`transaction.type_${typeFilter}` as any))] : []),
            ]}
            onRemoveFilter={(filter) => {
              // Clear matching filter
              if (statusFilter && translate((`transaction.${statusFilter}` as any)) === filter) setStatusFilter('');
              if (paymentMethodFilter && translate((`transaction.method_${paymentMethodFilter}` as any)) === filter) setPaymentMethodFilter('');
              if (typeFilter && translate((`transaction.type_${typeFilter}` as any)) === filter) setTypeFilter('');
            }}
            onClearAllFilters={() => { setStatusFilter(''); setPaymentMethodFilter(''); setTypeFilter(''); setDateFrom(''); setDateTo(''); }}
          />
        ) : (
          <EmptyState
            title={translate('transaction.empty_state')}
          />
        )
      ) : (
        <Table {...tableProps} opacity={isFetching ? 0.5 : 1} transition="opacity 0.2s">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th key={header.id} borderColor={borderColor} {...tableThProps}>
                    <Text {...tableHeaderTextProps}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Text>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr
                key={row.id}
                onClick={() => navigateToUser?.(row.original.userId)}
                {...tableRowProps}
              >
                {row.getVisibleCells().map((cell) => (
                  <Td
                    key={cell.id}
                    borderColor="transparent"
                    py={densityTokens[density].cellPy}
                    px={densityTokens[density].cellPx}
                    fontSize={densityTokens[density].fontSize}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </Card>
  );
}
